<template>
  <div>
    <v-dialog
      v-model="showDialog"
      :width="`${dialogWidth}px`"
      persistent
    >
      <template #activator="{ props }">
        <v-text-field
          v-bind="{ ...props, ...textFieldProps }"
          v-model="selectedDateAsLocaleString"
          :class="[
            extraSmallHeight ? 'extra-small-height' : '',
            dateIsValid && !textFieldProps['error-messages'] ? 'mb-2' : ''
          ]"
          :label="label"
          :data-testid="`${testid}-text-field`"
          readonly
          :hint="hint"
          :persistent-hint="showHint"
          :hide-details="(!showHint || dateIsValid) && !textFieldProps['error-messages']"
        >
          <template #prepend-inner>
            <v-icon
              :data-testid="`${testid}-calendar-icon`"
              :color="showDialog ? 'primary' : undefined"
              style="pointer-events: auto"
              @mousedown.stop.prevent
              @click.stop="handleIconClick"
            >
              mdi-calendar
            </v-icon>
          </template>
        </v-text-field>
      </template>
      <custom-date-picker
        v-if="datepickerType !== 'day'"
        v-model="selectedDateString"
        :testid="testid"
        :style="{ width: `${dialogWidth}px` }"
        :type="datepickerType"
        :max="maxDateUtc.toISOString()"
        :min="minDateUtc.toISOString()"
      />
      <div v-else>
        <div
          class="picker-row"
          :style="{ width: `${dialogWidth}px` }"
        >
          <v-date-picker
            v-show="showDatepicker"
            v-model="selectedDateString"
            :class="{ 'date-picker-with-time': showTimepicker }"
            :width="`${datePickerWidth}px`"
            :data-testid="`${testid}-date-picker`"
            :allowed-dates="allowedDates"
            color="primary"
            header-color="primary"
            hide-title
            first-day-of-week="1"
            weekday-format="short"
            :rounded="false"
            :max="maxDateUtc.toISOString()"
            :min="minDateUtc.toISOString()"
          >
            <template #header>
              {{ selectedDateAsLocaleString }}
            </template>
          </v-date-picker>
          <time-picker
            v-if="showTimepicker"
            v-model="selectedTime"
            :default-time="defaultTime"
            class="time-picker-panel"
            :style="{ width: `${timePickerWidth}px` }"
            :testid="testid"
            :date="selectedDate"
            :max="maxDateUtc.toISOString()"
            :min="minDateUtc.toISOString()"
          />
        </div>
      </div>
      <div class="d-flex picker-controls">
        <v-btn
          color="primary"
          variant="outlined"
          class="ma-3"
          :data-testid="`${testid}-reset-btn`"
          @click="resetDate"
        >
          {{ t("delete") }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          class="ma-3"
          :data-testid="`${testid}-ok-btn`"
          :disabled="!dateIsValid"
          @click="emitSelectedDate"
        >
          {{ t("ok") }}
        </v-btn>
      </div>
      <div
        v-if="!datesInOrder"
        data-testid="error-message-date-order"
        class="v-tooltip__content date_error_message"
        :style="{ width: `${dialogWidth}px` }"
      >
        {{ errorMessageDateOrder }}
      </div>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from "vue";
import { useDate } from 'vuetify';
import DatasetStore from "@/stores/dataset/dataset-module";
import {
  addTimezoneOffset,
  removeTimezoneOffset,
  Frequency,
  formatDateForDatepicker,
  formatDateForTextfield,
  getTimeString,
  localeDateString,
} from "@/util/datetime";
import CustomDatePicker from '@/components/common/picker/CustomDatePicker.vue';
import TimePicker from '@/components/common/picker/TimePicker.vue';
import { useI18n } from "vue-i18n";

interface TextFieldProps {
  "error-messages": string | string[];
  [prop: string]: any;
}

export default defineComponent({
  name: "TextFieldWithDatePicker",
  components: { CustomDatePicker, TimePicker },
  props: {
    value: { type: Object as () => Date | null, required: true },
    testid: { type: String, required: true },
    label: {
      type: [String, Object] as PropType<string | null>,
      required: false,
      default: null,
    },
    allowedDateRange: {
      type: null as unknown as PropType<[Date | undefined, Date | undefined]>,
      required: true,
    },
    selectedDateIsStartDate: { type: Boolean, required: true },
    referenceDate: { type: Object as () => Date | null, required: false, default: null },
    showHint: { type: Boolean, default: false },
    textFieldProps: {
      type: Object as () => TextFieldProps,
      default: () => ({ "error-messages": "" }),
    },
    extraSmallHeight: {
      type: Boolean,
      default: false
    }
  },
  emits: ["dateSelected"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const datasetStore = DatasetStore;
    const showDialog = ref(false);
    const showDatepicker = ref(true);
    const selectedDateString = ref<string | null>(null);
    const freq = ref<Frequency>(datasetStore.metadata!.frequency);
    const selectedTime = ref<string | null>(null);
    const selectedDateTimeString = ref<string | null>(null);
    const adapter = useDate();
    const selectedHour = ref<string | null>(null);
    const selectedMinute = ref<string | null>(null);
    const datePickerWidth = 350;
    const timePickerWidth = 160;
    const minDate = props.allowedDateRange[0];
    const maxDate = props.allowedDateRange[1];
    const minDateUtc = addTimezoneOffset(minDate!);
    const maxDateUtc = addTimezoneOffset(maxDate!);

    watch(
      () => props.value,
      () => {
        setSelectedDateStringToValue();
      },
      { immediate: true }
    );

    watch(showDialog, (isOpen) => {
      if (isOpen) {
        if (!selectedDateString.value) {
          selectedDateString.value = defaultDate.value;
          selectedTime.value = defaultTime.value;
        }
      }
    });

    function emitSelectedDate() {
      showDialog.value = false;
      emit("dateSelected", selectedDateTime.value as Date);
    };

    function setSelectedDateStringToValue(): void {
      if (props.value) {
        selectedDateTimeString.value = formatDateForDatepicker(
          props.value,
          freq.value
        ) as string;
        selectedDateString.value = formatDateForTextfield(props.value) as string;
        selectedTime.value = getTimeString(props.value) as string;
      } else {
        selectedDateString.value = null;
      }
    };

    function handleIconClick(): void {
      showDialog.value = true;
    };

    function resetDate(): void {
      showDialog.value = false;
      selectedDateString.value = null;
      selectedDateTimeString.value = null;
      selectedHour.value = null;
      selectedMinute.value = null;
      selectedTime.value = defaultTime.value;
      emitSelectedDate();
    };

    function allowedDates(val: string): boolean {
      const [, month, day] = adapter.toISO(val).split('-');
      const m = parseInt(month, 10)
      const d = parseInt(day, 10)
      let matchesFrequency = true;
      if (["1Y", "1YS", "P1Y"].includes(freq.value)) {
        matchesFrequency =  m === 1 && d === 1;
      } else if (freq.value === "QS-DEC") {
        matchesFrequency =  m % 3 === 0 && d === 1;
      } else if (["1M", "1MS", "P1M"].includes(freq.value)) {
        matchesFrequency =  d === 1;
      }
      return matchesFrequency;
    };

    const dateIsValid = computed<boolean>(() => {
      return selectedDate.value !== null && datesInOrder.value;
    });

    const datesInOrder = computed<boolean>(() => {
      if (
        props.referenceDate !== null &&
        props.referenceDate !== undefined &&
        selectedDateTime.value !== null
      ) {
        if (props.selectedDateIsStartDate) {
          return selectedDateTime.value <= props.referenceDate;
        } else {
          return selectedDateTime.value >= props.referenceDate;
        }
      } else {
        return true;
      }
    });

    const errorMessageDateOrder = computed<string>(() => {
      if (props.selectedDateIsStartDate) {
        return t("date_picker.error_message_start_after_end").toString();
      } else {
        return t("date_picker.error_message_end_before_start").toString();
      }
    });

    function formatTime(hours: number, minutes: number): string {
      const hh = hours.toString().padStart(2, '0');
      const mm = minutes.toString().padStart(2, '0');
      return `${hh}:${mm}`;
    }

    function roundDownToFrequency(date: Date, freq: string): Date {
      const result = new Date(date);
      const minutes = result.getMinutes();

      if (freq.endsWith('T')) {
        const step = parseInt(freq.split('T')[0], 10);
        const roundedMinutes = Math.floor(minutes / step) * step;
        result.setMinutes(roundedMinutes, 0, 0);
        return result;
      }

      if (freq.startsWith('PT') && freq.endsWith('M')) {
        const step = parseInt(freq.substring(2, freq.length - 1), 10);
        const roundedMinutes = Math.floor(minutes / step) * step;
        result.setMinutes(roundedMinutes, 0, 0);
        return result;
      }

      if (['1H', 'PT1H'].includes(freq)) {
        result.setMinutes(0, 0, 0);
        return result;
      }

      return result;
    }

    const defaultDateTime = computed(() => {
      const nowUtc = addTimezoneOffset(new Date());
      const todayMidnightUtc = new Date(nowUtc.getFullYear(), nowUtc.getMonth(), nowUtc.getDate());

      let defaultDate: Date;
      let defaultTimeStr: string;

      if (props.selectedDateIsStartDate) {
        if (minDateUtc && minDateUtc.toDateString() === nowUtc.toDateString()) {
          defaultDate = minDateUtc;
          defaultTimeStr = formatTime(minDateUtc.getHours(), minDateUtc.getMinutes());
        } else if (maxDateUtc && maxDateUtc < todayMidnightUtc) {
          defaultDate = maxDateUtc;
          defaultTimeStr = '00:00';
        } else {
          defaultDate = nowUtc;
          defaultTimeStr = '00:00';
        }
      } else {
        if (maxDateUtc && maxDateUtc < nowUtc) {
          defaultDate = maxDateUtc;
          defaultTimeStr = formatTime(maxDateUtc.getHours(), maxDateUtc.getMinutes());
        } else {
          defaultDate = nowUtc;
          const lastAvailableTimeStep = roundDownToFrequency(nowUtc, freq.value);
          defaultTimeStr = formatTime(lastAvailableTimeStep.getHours(), lastAvailableTimeStep.getMinutes());
        }
      }
      const defaultDateStr = removeTimezoneOffset(defaultDate).toISOString().split('T')[0];
      return { date: defaultDateStr, time: defaultTimeStr };
    });

    const defaultDate = computed(() => defaultDateTime.value.date);
    const defaultTime = computed(() => defaultDateTime.value.time);


    const hint = computed<string>(() => {
      return props.showHint && !dateIsValid.value
        ? t("download_form.date_error_message").toString()
        : "";
    });

    const selectedDateAsLocaleString = computed<string>(() => {
      const date = selectedDateTime.value;
      if (date === null) {
        return "";
      }
      return localeDateString(
        addTimezoneOffset(date),
        locale.value,
        freq.value
      );
    });

    const selectedDate = computed<Date | null>(() => {
      if (selectedDateString.value === null) {
        return null;
      }
      return new Date(selectedDateString.value);
    });

    const selectedDateTime = computed<Date | null>(() => {
      if (!selectedDateString.value) return null;
      const date = new Date(selectedDateString.value);
      const timeStr = selectedTime.value ?? defaultTime.value;
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        date.setHours(hours, minutes);
        return removeTimezoneOffset(date);
      }
      return date;
    });

    const showTimepicker = computed<boolean>(() => {
      return ![
        "1D",
        "P1D",
        "1M",
        "1MS",
        "P1M",
        "QS-DEC",
        "1Y",
        "1YS",
        "P1Y",
      ].includes(freq.value);
    });

    const dialogWidth = computed(() => {
      return showTimepicker.value ? datePickerWidth + timePickerWidth : datePickerWidth;
    });

    const timeIsValid = computed<boolean>(() => {
      return (
        selectedTime.value !== null &&
        selectedTime.value.split(":").length === 2
      );
    });

    const datepickerType = computed<string>(() => {
      if (["1M", "1MS", "P1M"].includes(freq.value)) {
        return "month";
      } else if (["QS-DEC"].includes(freq.value)) {
        return "season";
      } else if (["1Y", "1YS", "P1Y"].includes(freq.value)) {
        return "year";
      } else {
        return "day";
      }
    });

    return {
      t,
      showDialog,
      showDatepicker,
      selectedDateString,
      freq,
      selectedTime,
      selectedDateTimeString,
      emitSelectedDate,
      handleIconClick,
      resetDate,
      allowedDates,
      dateIsValid,
      datesInOrder,
      errorMessageDateOrder,
      hint,
      selectedDateAsLocaleString,
      selectedDate,
      selectedDateTime,
      showTimepicker,
      dialogWidth,
      datePickerWidth,
      timePickerWidth,
      timeIsValid,
      datepickerType,
      defaultTime,
      minDateUtc,
      maxDateUtc,
    };
  },
});
</script>

<style scoped>
.date-picker-with-time {
  border-top-right-radius: 0;
}

.picker-controls {
  background-color: white;
}

.picker-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  height: 420px;
  background-color: white;
}

.time-picker-panel {
  width: 275px;
  flex-shrink: 0;
}

:deep(.v-date-picker .v-picker__header) {
  font-size: 25px;
  height: 70px;
  padding: 15px;
}

:deep(.v-time-picker .v-picker__header) {
  font-size: 40px;
  height: 70px;
}

:deep(.v-time-picker-controls__time) {
  height: 40px;
}

:deep(.v-time-picker-controls__time__separator) {
  font-size: 30px;
  width: 100%;
  color: rgb(var(--v-theme-on-primary));
}

:deep(.v-time-picker-controls__time__field .v-field__input) {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
  opacity: 100 !important;
}

:deep(.v-time-picker > div:first-child) {
  background-color: rgb(var(--v-theme-primary));
  height: 70px;
}

:deep(.v-time-picker-controls) {
  /* set title height of time picker to same height as for date picker */
  height: 50px;
}

:deep(.v-date-picker > div:first-child) {
  background-color: rgb(var(--v-theme-primary));
  height: 70px;
}

:deep(.v-messages__message) {
  /*Without this the message below the textfield is cropped at the top in some browsers*/
  line-height: 16px !important ;
}

.date_error_message {
  margin-top: 20px;
  opacity: 1;
  border-radius: 4px;
  position: absolute;
  top: 462px;
  line-height: 22px;
  padding: 5px 16px;
}

:deep(.v-field-label) {
  /* make label clickable for chrome under windows */
  pointer-events: none;
}

:deep(.extra-small-height .v-field) {
  height: 32px !important;
}

:deep(.extra-small-height .v-field__input) {
  height: 32px !important;
  min-height: 32px !important;
}
</style>
